import { useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import Error from '../components/ErrorBoundary';
import Navbar from '../components/Navbar';
import cookies from '../utils/cookies';
import { CatalogStateContext, CatalogDispatchContext } from '../store/CatalogContext';
import CuratedCatalog from '../components/CuratedCatalog';
import API from '../services/apiService';
import getUniqueRelatedUpsellIds from '../utils/getUniqueRelatedUpsellIds';
import type { Product } from '../types/product';

const Recommended = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { prevProductIds, tp, cc, gid } = Object.fromEntries([...searchParams]);

  const catalogState = useContext(CatalogStateContext);
  const catalogDispatch = useContext(CatalogDispatchContext);

  // Determine redirect condition once per render
  const shouldRedirect = prevProductIds || cookies.get('prevProductIds') ? false : true;

  // Redirect effect (side-effect separated from render)
  useEffect(() => {
    if (shouldRedirect) navigate('/', { replace: true });
  }, [shouldRedirect, navigate]);

  // fetch products for the catalog
  useEffect(() => {
    if (shouldRedirect) return; // do not fetch if redirecting

    const controller = new AbortController();

    const productIds = prevProductIds || cookies.get('prevProductIds');

    // we dont want to call the API again if the products are already loaded
    // we are using the purchased products as a trigger because the related products also depend on it
    if (!catalogState.products.purchased && productIds) {
      const fetchProducts = async () => {
        catalogDispatch({
          type: 'UPDATE',
          payload: {
            isLoading: true,
          },
        });

        try {
          const baseParams = {
            testPrice: tp,
            groupId: gid,
            coupon: cc,
            // fullSiteBuild: true, // default is true
            includeUpsells: true,
          };

          const params = {
            productIds: productIds ? productIds.replaceAll(' ', '').split(',') : [],
            ...baseParams,
          };

          // fetch purchased products
          const purchasedRes = await API.getProductsByIds(params, controller.signal);

          const purchasedProducts = purchasedRes.data as Product[];

          // setting test price cookie if it is applied to any product
          const isTestPriceApplied = purchasedProducts.some(
            (product) => product?.pricing?.typePricing?.toLowerCase() === tp?.toLowerCase()
          );
          if (tp && isTestPriceApplied) {
            cookies.set('_up4tp', tp.toLowerCase(), 30);
          }

          // if queryGroupId or defaultGroupId is used on any product, set it as a cookie
          const isGroupIdApplied = purchasedProducts.some(
            (product) => product.pricing.groupId?.toLowerCase() === gid?.toLowerCase()
          );
          if (gid && isGroupIdApplied) {
            cookies.set('_up4gid', gid.toLowerCase(), 30);
          }

          // if coupon is applied to any product, set it as a cookie
          const isCouponApplied = purchasedProducts.some((product) => product.pricing.discountSource === 'Coupon');
          if (cc && isCouponApplied) {
            cookies.set('_up4cc', cc.toLowerCase(), 30);
          }

          // Order the purchased products according to the original productIds array
          // API ordering is not reliable, so we need to sort it ourselves
          const orderedPurchased = params.productIds
            .map((id) => purchasedRes.data.find((item: Product) => item.id.toLowerCase() === id.toLowerCase()))
            .filter(Boolean);

          // Derive related IDs from ordered list (order preservation if util respects input order)
          const relatedProductIds = getUniqueRelatedUpsellIds(orderedPurchased);

          let relatedProducts = [];
          if (relatedProductIds.length > 0) {
            const relatedParams = {
              productIds: relatedProductIds,
              ...baseParams,
              includeUpsells: false,
            };
            const relatedRes = await API.getProductsByIds(relatedParams, controller.signal);
            relatedProducts = relatedRes.data;
          }

          // Order the related products according to the relatedProductIds array
          const orderedRelated = relatedProductIds
            .map((id) => relatedProducts.find((item: Product) => item.id.toLowerCase() === id.toLowerCase()))
            .filter(Boolean);

          // Single dispatch after all async work completes
          catalogDispatch({
            type: 'UPDATE_PRODUCTS',
            payload: {
              purchased: orderedPurchased,
              related: [...orderedRelated],
            },
          });

          // if there's no issue with the API calls set the productIds as a cookie
          if (productIds) {
            cookies.set('prevProductIds', productIds, 30);
          }

          // reset filters after fetching products successfully
          const updatedView = {
            purchased: {
              ...catalogState.view.purchased,
              filters: {
                searchQuery: null,
                collection: null,
                category: null,
                subCategory: null,
                individualBundle: null,
              },
            },
            related: {
              ...catalogState.view.related,
              filters: {
                searchQuery: null,
                collection: null,
                category: null,
                subCategory: null,
                individualBundle: null,
              },
            },
          };

          catalogDispatch({
            type: 'VIEW',
            payload: {
              ...updatedView,
            },
          });

          sessionStorage.setItem(
            'catalogView',
            JSON.stringify({
              ...catalogState.view,
              ...updatedView,
            })
          );
        } catch (err: unknown) {
          // Ensure error is typed to allow access to 'name'
          const error = err as { name?: string; response?: { data?: any } };
          if (error?.name === 'CanceledError') {
            console.error('Request Aborted');
          } else if (error?.name === 'AxiosError') {
            // console.error(error);
            console.error(error?.response?.data);
          } else {
            console.error(error);
          }

          // show error message or reload the page
          if ((error?.name ?? '') !== 'CanceledError') {
            catalogDispatch({
              type: 'ERROR',
              payload: {
                isLoading: false,
                error: error?.response?.data || 'General Error',
              },
            });
          }
        }
      };

      fetchProducts();
    }

    return () => controller.abort();
  }, [shouldRedirect, catalogState.products.purchased, catalogState.view, catalogDispatch, prevProductIds, tp, gid, cc]);

  if (shouldRedirect) return null;

  return (
    <>
      <title>Recommended Products</title>
      <meta name="description" content="Recommended products for you." />

      <div className="container-fluid but-fixed py-3 py-md-4 py-lg-4">
        <div className="row justify-content-center">
          <Navbar activePage="recommended" />

          {/* Purchased Products */}
          <div className="purchased-products col-12">
            <h1 className="fs-4 text-theme-success mb-3">Previously Purchased</h1>

            <CuratedCatalog group="purchased" prefix="purchased" gridCount={3} />
          </div>

          {/* Related Products */}
          <div className="related-products col-12 mt-4 mt-md-5">
            <h1 className="fs-4 text-theme-success mb-3">Others Also Purchased</h1>

            <CuratedCatalog group="related" prefix="related" gridCount={4} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Recommended;

export const ErrorBoundary = () => {
  return <Error fromPage="Recommended Products Page" />;
};
